import { useRef, useState, useEffect } from "react";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus, InteractionRequiredAuthError, AccountInfo } from "@azure/msal-browser";

import styles from "./Chat.module.css";
import { Modal } from "@fluentui/react";

import { useBoolean } from '@fluentui/react-hooks';

import LoadingIframe from 'react-loading-iframe';

import { chatApi, AskResponse, ChatRequest, ChatTurn } from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { ExampleList } from "../../components/Example";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { ClearChatButton } from "../../components/ClearChatButton";
import { callMsGraph } from "../../utils/MsGraphApiCall";
import { GraphData } from "../../utils/ProfileData";
import { loginRequest } from "../../pages/login/authConfig";
import Skeleton from 'react-loading-skeleton';

const Chat = () => {
    const { instance, inProgress,accounts } = useMsal();
    const [graphData, setGraphData] = useState<null | GraphData>(null);
    const [accessToken, setAccessToken] = useState<null | string>(null);
    useEffect(() => {
        if (inProgress === 'none' && accounts.length > 0) {
            const request = {
                account: accounts[0],
                scopes: ["User.Read"]
            };
            // Retrieve an access token
            instance.acquireTokenSilent(request)
                .then(response => {
                    if (response.accessToken) {
                        //console.log('accessToken', response.accessToken);
                        setAccessToken(response.accessToken);
                        // return response.accessToken;
                    }
                })
                .catch(error => console.log('token error', error));
        }
    }, [inProgress, accounts, instance]);

    useEffect(() => {
        if (!graphData && inProgress === InteractionStatus.None) {
            callMsGraph()
                .then(response => setGraphData(response))
                .catch(e => {
                    if (e instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect({
                            ...loginRequest,
                            account: instance.getActiveAccount() as AccountInfo
                        });
                    }
                });
        }
    }, [inProgress, graphData, instance]);

    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [retrieveCount, setRetrieveCount] = useState<number>(3);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(false);

    const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: AskResponse][]>([]);

    const makeApiRequest = async (question: string) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        try {
            const history: ChatTurn[] = answers.map(a => ({ user: a[0], bot: a[1].answer }));
            const request: ChatRequest = {
                userquestion: question,
                debug: "yes",
                useremail: "" + graphData?.mail,
                history: [...history],
                token:accessToken,
                overrides: {
                    promptTemplate: promptTemplate.length === 0 ? undefined : promptTemplate,
                    excludeCategory: excludeCategory.length === 0 ? undefined : excludeCategory,
                    top: retrieveCount,
                    semanticRanker: useSemanticRanker,
                    semanticCaptions: useSemanticCaptions,
                    suggestFollowupQuestions: useSuggestFollowupQuestions
                }
            };
            const result = await chatApi(request);
            setAnswers([...answers, [question, result]]);
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" }), [isLoading]);

    const clearChat = () => {
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
    };

    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
        showModal();
    };

    function dateToday() {
        const today = (new Date().toLocaleDateString('en-in', { weekday: "long", year: "numeric", month: "long", day: "numeric" })); // e.g. Wednesday, 23 August, 2023
        const dtSplit = today.split(/[ ,]+/);
        return `${dtSplit[0]}, ${dtSplit[1]} ${dtSplit[2]} ${dtSplit[3]}`;
    }

    function renderIframeLoading() {
        return <Skeleton count={5} />
    }

    return (
        <>
            <UnauthenticatedTemplate>
                <a href="/">Please sign in</a>
            </UnauthenticatedTemplate>

            <AuthenticatedTemplate>
                <div className={`container px-0 d-flex flex-column shadow-sm ${styles.container}`}>

                    {/* chat conversation */}
                    {
                        !lastQuestionRef.current
                            // tampilkan example list pertaanyaan
                            ? (
                                <div className={`d-flex flex-column justify-content-between align-items-center gap-2 px-2 ${styles.chatMinHv}`}>
                                    <div className='flex-grow-1 fs-1 fw-semibold flex-row d-flex align-items-center gap-2'>
                                        <span className={styles.labelXLPrimary}>Chat with</span>
                                        <span className={styles.labelXLAccent}>your data</span>
                                    </div>
                                    <span className="fs-4 text-body-secondary">Ask anything or try an example</span>
                                    <ExampleList onExampleClicked={onExampleClicked} />
                                </div>
                            )

                            // chat thread
                            : (
                                <main className={`d-flex flex-column justify-content-between align-items-center ${styles.chatMinHv}`}>
                                    <section className={`w-100 d-flex gap-2 align-content-center align-items-center px-4 ${styles.chatTopbar}`}>
                                        <span className='flex-grow-1 text-center'>{dateToday()}</span>
                                        <ClearChatButton className="text-danger" onClick={clearChat} disabled={!lastQuestionRef.current || isLoading} />
                                    </section>

                                    <section className={`${styles.chatPaddingX} flex-grow-1 d-flex flex-column gap-2 w-100 mt-4`}>
                                        {/* answer converstion */}
                                        {answers.map((answer, index) => (
                                            <div key={index}>
                                                <UserChatMessage message={answer[0]} />
                                                <div className={styles.chatMessageGpt}>
                                                    <Answer
                                                        key={index}
                                                        answer={answer[1]}
                                                        isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                        onCitationClicked={c => onShowCitation(c, index)}
                                                        onThoughtProcessClicked={() => { }}
                                                        onSupportingContentClicked={() => { }}
                                                        onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                        showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                                    />
                                                </div>
                                            </div>
                                        ))}

                                        {/* Loading indicator */}
                                        {isLoading && (
                                            <>
                                                <UserChatMessage message={lastQuestionRef.current} />
                                                <div className={styles.chatMessageGptMinWidth}>
                                                    <AnswerLoading />
                                                </div>
                                            </>
                                        )}

                                        {/* Error message */}
                                        {error ? (
                                            <>
                                                <UserChatMessage message={lastQuestionRef.current} />
                                                <div className={styles.chatMessageGptMinWidth}>
                                                    <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                                </div>
                                            </>
                                        ) : null}

                                        <div ref={chatMessageStreamEnd} />
                                    </section>
                                </main>
                            )
                    }

                    {/* Input question-box */}
                    <div className={`w-100 py-5 ${styles.chatPaddingX} ${styles.chatInput}`}>
                        <QuestionInput
                            clearOnSend
                            placeholder="Type a new question here"
                            disabled={isLoading}
                            onSend={question => makeApiRequest(question)}
                        />
                    </div>
                </div>

                <Modal
                    titleAriaId={'Hallo'}
                    isOpen={isModalOpen}
                    onDismiss={hideModal}
                    containerClassName={styles.modalContainer}
                    isBlocking={false}
                >
                    <LoadingIframe title="Citation" skeleton={renderIframeLoading()} src={activeCitation} />
                </Modal>
            </AuthenticatedTemplate>
        </>
    );
};

export default Chat;
